import React, { useRef, useState, useEffect } from 'react';
import { getSession } from '../getSession';
import {
  Modal,
  Button,
  Form,
  InputGroup,
  ProgressBar,
  Table,
  Dropdown,
  Row,
  Col,
  Pagination,
} from 'react-bootstrap';
import {
  Table as TableIcon,
  Grid3x3Gap,
  ThreeDotsVertical,
  X as CloseIcon,
} from 'react-bootstrap-icons';
import "./MediaLibrary.css"
import { getStorageLibraryList, sendMessage, getQuota, uploadbulkFile } from '../api/MediaLib';
import { ValidateAppUserToken } from '../api/chatbotbuilderapi';
import Swal from 'sweetalert2';
import { is } from 'date-fns/locale';

// Loading context may not be present in all workspaces — provide a safe fallback
let useLoading = null;
try {
  // eslint-disable-next-line global-require
  useLoading = require('../context/LoadingContext').useLoading;
} catch (e) {
  useLoading = () => ({ setLoading: () => { } });
}

const MediaLibrary = ({ show, onclose, onSelectMedia, filterOption = 'All', cardType, cardOrientation, cardHeight, mediaAlignment, cardWidth, channel, leadId }) => {
  const { userId, LoginName } = getSession();
  const [filter, setFilter] = useState(filterOption);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [mediaItems, setMediaItems] = useState([]);
  const [showUploader, setShowUploader] = useState(false);
  const [showTable, setShowTable] = useState(true);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [quota, setQuota] = useState(null);
  const [gettoken, setGetToken] = useState('');
  const [success, setSuccess] = useState("")
  const [error, setError] = useState("")
  const { setLoading } = useLoading();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  // Helper function to get max file size based on file kind
  const getMaxFileSize = (fileKind) => {
    const fileSizeLimits = {
      'image': 5 * 1024 * 1024,      // 5 MB
      'audio': 16 * 1024 * 1024,     // 16 MB
      'video': 16 * 1024 * 1024,     // 16 MB
      'other': 100 * 1024 * 1024     // 100 MB
    };
    return fileSizeLimits[fileKind] || fileSizeLimits['other'];
  };

  // Helper function to get file size in MB for display
  const getFileSizeInMB = (fileKind) => {
    const maxSize = getMaxFileSize(fileKind);
    return Math.round((maxSize / 1024 / 1024) * 10) / 10; // Round to 1 decimal
  };

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const result = await ValidateAppUserToken(userId);
      let token = '';

      if (result?.Status === 1) {
        const parsedData = JSON.parse(result.Data);
        const userInfo = parsedData[0];
        token = userInfo?.token || '';
        setGetToken(token);
      } else {
        setLoading(false);
        return;
      }

      const validLeadId = Number(leadId) > 0;
      const mediaPath = validLeadId ? `${userId}/Restricted/ticket/${leadId}/Documents/` : `${userId}/Restricted/Lead/`;
      const data = await getStorageLibraryList(
        mediaPath,
        userId,
        userId,
        token
      );
      const list = typeof data === 'string' ? JSON.parse(data) : data;

      const formatted = list
        .filter((item) => item.Type === 'File' || item.Type === 'Image' || item.File_Type === 'Image')
        .map((item) => ({
          id: item.FilePath || item.Download_Link,
          name: item.Name,
          size: item.Size,
          type: item.File_Type,
          date: item.Created_On,
          thumbnail: item.Download_Link,
          created_On: item.Created_On,
        }))
        .sort((a, b) => {
          const parseDate = (dateStr) => {
            if (!dateStr) return null;
            try {
              // Handle "21-Feb-2026 10:22 PM" or "21/02/2026" or "2026-02-20"
              const parts = dateStr.split(/[\sT]/);
              const datePart = parts[0];
              const timePart = parts[1];
              const ampm = parts[2];

              let day, month, year;
              if (datePart.includes('-')) {
                const dparts = datePart.split('-');
                if (dparts[0].length === 4) { [year, month, day] = dparts; month = parseInt(month) - 1; }
                else { [day, month, year] = dparts; }
              } else if (datePart.includes('/')) {
                [day, month, year] = datePart.split('/');
              }

              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              let monthIndex = monthNames.indexOf(month);
              if (monthIndex === -1 && month !== undefined) monthIndex = parseInt(month) - 1;
              if (isNaN(monthIndex)) return null;

              const date = new Date(parseInt(year), monthIndex, parseInt(day));

              if (timePart) {
                let [hours, minutes, seconds] = timePart.split(':');
                hours = parseInt(hours);
                if (ampm === 'PM' && hours < 12) hours += 12;
                if (ampm === 'AM' && hours === 12) hours = 0;
                date.setHours(hours, parseInt(minutes) || 0, parseInt(seconds) || 0, 0);
              }
              return date;
            } catch (e) {
              return null;
            }
          };

          const dateA = parseDate(a.created_On);
          const dateB = parseDate(b.created_On);

          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;

          return dateB - dateA;
        });
      setMediaItems(formatted);

      const quotaResponse = await getQuota(userId, userId);
      setQuota(quotaResponse);
      return formatted;
    } catch (error) {
      console.error('Error fetching media or quota:', error);
    } finally {
      setLoading(false);
    }
  };

  // When this modal opens, lower z-index of other existing modals/backdrops
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!show) return undefined;

    const modified = [];
    try {
      const otherModals = Array.from(document.querySelectorAll('.modal.show'))
        .filter((m) => !m.classList.contains('media-library-modal'));
      otherModals.forEach((m) => {
        modified.push({ el: m, prev: m.style.zIndex });
        m.style.zIndex = '20000';
      });

      const otherBackdrops = Array.from(document.querySelectorAll('.modal-backdrop'))
        .filter((b) => !b.classList.contains('media-library-backdrop'));
      otherBackdrops.forEach((b) => {
        modified.push({ el: b, prev: b.style.zIndex });
        b.style.zIndex = '20000';
      });
    } catch (e) {
      // ignore DOM errors
    }

    return () => {
      modified.forEach((m) => {
        try { m.el.style.zIndex = m.prev || ''; } catch (e) { }
      });
    };
  }, [show]);

  useEffect(() => {
    if (show) {
      fetchMedia();
    }
  }, [show, userId, leadId]);

  const handleMediaSelection = (selectedItem) => {
    if (!selectedItem) return;

    // Only validate for image/video
    const ext = selectedItem.name.split('.').pop().toLowerCase();
    const isImage = ["jpg", "jpeg", "png", "gif"].includes(ext);
    const isVideo = ["mp4"].includes(ext);

    // --- Validation rules ---
    let requiredWidth = 1440, requiredHeight = 480, maxSize = 1 * 1024 * 1024;
    if (cardType === 'rich_card') {
      if (cardOrientation === 'vertical' && cardHeight === 'short') {
        requiredWidth = 1440; requiredHeight = 480;
      } else if (cardOrientation === 'vertical' && cardHeight === 'medium') {
        requiredWidth = 1440; requiredHeight = 720;
      } else if (cardOrientation === 'horizontal' && mediaAlignment === 'left') {
        requiredWidth = 768; requiredHeight = 1024;
      } else if (cardOrientation === 'horizontal' && mediaAlignment === 'right') {
        requiredWidth = 768; requiredHeight = 1024;
      }
      maxSize = isVideo ? 5 * 1024 * 1024 : 1 * 1024 * 1024;
    }
    else if (cardType === 'carousel' || cardType === 'carouselCard') {
      maxSize = isVideo ? 5 * 1024 * 1024 : 1 * 1024 * 1024;
      if (cardWidth === 'SMALL' && cardHeight === 'SHORT') {
        requiredWidth = 960; requiredHeight = 768;
      } else if (cardWidth === 'SMALL' && cardHeight === 'MEDIUM') {
        requiredWidth = 576; requiredHeight = 720;
      }
      else if (cardWidth === 'MEDIUM' && cardHeight === 'SHORT') {
        requiredWidth = 1440; requiredHeight = 720;
      } else if (cardWidth === 'MEDIUM' && cardHeight === 'MEDIUM') {
        requiredWidth = 1440; requiredHeight = 1080;
      }
    }

    const completeSelection = () => {
      onSelectMedia({
        ...selectedItem,
        url: selectedItem.thumbnail,
      });
      setLoading(false);
      onclose();
    };

    // Validate image
    if (isImage) {
      if (selectedItem.size && Number(selectedItem.size) > maxSize) {
        alert(`Image file size must be less than ${maxSize / 1024 / 1024} MB.`);
        return;
      }
      const img = new window.Image();
      img.crossOrigin = 'Anonymous';
      img.onload = function () {
        /*
        if (!(img.width === requiredWidth && img.height === requiredHeight)) {
          alert(`The image you specify must have a resolution of ${requiredWidth} x ${requiredHeight} pixels.`);
          return;
        }
        */
        completeSelection();
      };
      img.onerror = function () {
        alert('Could not load image for validation.');
      };
      img.src = selectedItem.thumbnail;
      return;
    } else if (isVideo) {
      if (selectedItem.size && Number(selectedItem.size) > maxSize) {
        alert(`Video file size must be less than ${maxSize / 1024 / 1024} MB.`);
        return;
      }
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = function () {
        /*
        if (!(video.videoWidth === requiredWidth && video.videoHeight === requiredHeight)) {
          alert(`The video you specify must have a resolution of ${requiredWidth} x ${requiredHeight} pixels.`);
          return;
        }
        */
        completeSelection();
      };
      video.onerror = function () {
        alert('Could not load video for validation.');
      };
      video.src = selectedItem.thumbnail;
      return;
    }

    // For other types, just select
    completeSelection();
  };

  const toggleSelectMedia = (id) => {
    setSelectedMedia([id]);
  };

  const resetSelection = () => {
    setSelectedMedia([]);
    setSearchTerm('');
    setFilter('Images');
  };

  const handleUploadClick = () => {
    if (viewMode === 'grid') {
      setShowUploader(!showUploader);
    } else if (viewMode === 'table') {
      setShowUploader((prev) => !prev);
    }
  };

  const handleViewToggle = () => {
    if (viewMode === 'grid') {
      setViewMode('table');
      setShowTable(true);
    } else {
      if (showUploader) {
        setShowTable(!showTable);
      } else {
        setViewMode('grid');
      }
    }
  };

  const handleFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };


  const handleFileChange = (event) => {
    const file = event.target ? event.target.files?.[0] : event;
    if (file) {
      const previewUrl = URL.createObjectURL(file);

      let fileKind = 'other';
      if (file.type.startsWith('image/')) fileKind = 'image';
      else if (file.type.startsWith('video/')) fileKind = 'video';
      else if (file.type.startsWith('audio/')) fileKind = 'audio';
      else if (file.type.startsWith('application/pdf/')) fileKind = 'pdf';
      else if (file.type.startsWith('application/doc/')) fileKind = 'doc';
      setUploadedFile({ file, previewUrl, kind: fileKind });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };



  const handleRemoveFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const filteredItems = mediaItems.filter((item) => {
    const name = item.name?.toLowerCase() || "";
    const ext = name.split('.').pop();

    const videoExts = ["mp4", "mkv", "mov", "avi", "webm", "flv", "wmv", "m4v", "3gp", "mpeg"];
    const audioExts = ["mp3", "wav", "aac", "ogg", "flac", "m4a"];
    const imageExts = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];

    const fileTypeMatches =
      filter === 'All' ||
      (filter === 'Images' && (item.type === 'Image' || imageExts.includes(ext))) ||
      (filter === 'Video' && videoExts.includes(ext)) ||
      (filter === 'Audio' && audioExts.includes(ext)) ||
      (filter === 'PDF' && item.type === 'PDF') ||
      (filter === 'Docs' && item.type === 'Docs') ||
      (filter === 'Other' && !['Image', 'PDF', 'Docs'].includes(item.type) && !videoExts.includes(ext) && !audioExts.includes(ext) && !imageExts.includes(ext));

    const searchMatches = name.includes(searchTerm.toLowerCase());

    return fileTypeMatches && searchMatches;
  });

  // Pagination calculations
  const totalRecords = filteredItems.length;
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

  // Reset to page 1 when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm]);

  return (
    <>
      <Modal
        show={show}
        onHide={() => {
          setLoading(false);
          onclose();
        }}
        size="xl"
        backdrop="static"
        container={typeof document !== 'undefined' ? document.body : undefined}
        dialogClassName="media-library-modal"
        backdropClassName="media-library-backdrop"
        style={{ zIndex: 9999999 }}
      >
        <Modal.Header>
          <div className="flex-grow-1 pe-3 d-flex justify-content-between align-items-center flex-wrap">
            <div className="d-flex align-items-center flex-wrap">
              <strong>Media Library</strong>
              <span className="text-success ms-3">{LoginName}</span>
            </div>
            <div style={{ minWidth: '300px' }}>
              <ProgressBar
                now={
                  quota
                    ? (quota?.UsedStorage / (quota?.TotalQuota * 1024 * 1024 * 1024)) * 100
                    : 0
                }
                className="my-progress-bar"
                label={`${quota?.UsedStorageFormatted} of ${quota?.TotalQuota} ${quota?.StorageUnit} used`}
                style={{ height: '20px', borderRadius: "20px" }}
              />

            </div>
          </div>
          <CloseIcon 
            size={28} 
            style={{ cursor: 'pointer', color: '#6c757d', marginLeft: '10px' }} 
            onClick={() => {
              setLoading(false);
              onclose();
            }} 
          />
        </Modal.Header>

        <Modal.Body scrollable={true}>
          <div>
            <Row className="mb-3">
              <Col md={6} className="d-flex flex-wrap align-items-center">
                <Form.Check
                  inline
                  type="radio"
                  label="All"
                  checked={filter === 'All'}
                  onChange={() => setFilter('All')}
                  disabled={filterOption && filterOption !== 'All'}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="Images"
                  checked={filter === 'Images'}
                  onChange={() => setFilter('Images')}
                  disabled={filterOption && filterOption !== 'All' && filterOption !== 'Images'}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="Video"
                  checked={filter === 'Video'}
                  onChange={() => setFilter('Video')}
                  disabled={filterOption && filterOption !== 'All' && filterOption !== 'Video'}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="Audio"
                  checked={filter === 'Audio'}
                  onChange={() => setFilter('Audio')}
                  disabled={filterOption && filterOption !== 'All' && filterOption !== 'Audio'}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="PDF"
                  checked={filter === 'PDF'}
                  onChange={() => setFilter('PDF')}
                  disabled={filterOption && filterOption !== 'All' && filterOption !== 'PDF'}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="Docs"
                  checked={filter === 'Docs'}
                  onChange={() => setFilter('Docs')}
                  disabled={filterOption && filterOption !== 'All' && filterOption !== 'Docs'}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="Other"
                  checked={filter === 'Other'}
                  onChange={() => setFilter('Other')}
                  disabled={filterOption && filterOption !== 'All' && filterOption !== 'Other'}
                />
              </Col>

              <Col md={6} className="d-flex justify-content-end align-items-center flex-wrap gap-2">
                <InputGroup style={{ maxWidth: '200px' }}>
                  <Form.Control
                    placeholder="Search media"
                    value={searchTerm}
                    size="sm"
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>

                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleViewToggle}
                >
                  {viewMode === 'grid' ? <TableIcon /> : <Grid3x3Gap />}
                </Button>

                <Button
                  variant="btn btn-outline-success"
                  size="sm"
                  onClick={handleUploadClick}
                >
                  {showUploader ? 'Cancel' : '+ Upload'}
                </Button>

                <Dropdown align="end">
                  <Dropdown.Toggle
                    variant="light"
                    id="dropdown-basic"
                    size="sm"
                    style={{ border: 'none' }}
                  >
                    <ThreeDotsVertical size={20} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => fetchMedia()}>Refresh Library</Dropdown.Item>
                    <Dropdown.Item href="#/download">Download</Dropdown.Item>
                    <Dropdown.Item href="#/copy">Copy</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
            </Row>
          </div>

          {showUploader && (
            <>
              <p style={{ color: '#00cf6eff' }}>{success}</p>
              <p style={{ color: '#f00' }}>{error}</p>
              <div
                style={{
                  border: '2px dashed green',
                  padding: '40px',
                  textAlign: 'center',
                  color: 'green',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  borderRadius: '10px',
                }}
                onClick={!uploadedFile ? handleFileClick : undefined}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >

                {!uploadedFile ? (
                  <>
                    Drag And Drop A File Or Select Add File
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />
                  </>
                ) : (
                  <>
                    {uploadedFile && (
                      <div style={{ textAlign: 'center' }}>
                        {uploadedFile.kind === 'image' && (
                          <img
                            src={uploadedFile.previewUrl}
                            alt={uploadedFile.file.name}
                            style={{ maxWidth: '200px', maxHeight: '200px', marginBottom: '10px', borderRadius: "10px" }}
                          />
                        )}

                        {uploadedFile.kind === 'video' && (
                          <video
                            src={uploadedFile.previewUrl}
                            controls
                            style={{ maxWidth: '200px', maxHeight: '200px', marginBottom: '10px', borderRadius: "10px" }}
                          />
                        )}

                        {uploadedFile.kind === 'audio' && (
                          <audio
                            src={uploadedFile.previewUrl}
                            controls
                            style={{ width: '200px', marginBottom: '10px' }}
                          />
                        )}

                        {uploadedFile.kind === 'other' && (
                          <p>No preview available. File type: {uploadedFile.file.type}</p>
                        )}

                        <div>{uploadedFile.file.name}</div>
                        <Button variant="danger" size="sm" onClick={handleRemoveFile}>
                          Remove Uploaded File
                        </Button>
                      </div>
                    )}

                  </>
                )}
              </div>
            </>
          )}

          {viewMode === 'grid' && !showUploader && (
            <div style={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
              <div className="grid-view-gallery">
                {currentItems.map((item) => (
                  <div
                    key={item.id}
                    className="card m-2"
                    style={{ width: '100%', cursor: 'pointer' }}
                    onClick={() => toggleSelectMedia(item.id)}
                  >
                    <img src={item.thumbnail} alt={item.name} className="card-img-top" />
                    <div className="card-body text-center p-2 flex gap-2">
                      <Form.Check
                        type="radio"
                        checked={selectedMedia.includes(item.id)}
                        onChange={() => toggleSelectMedia(item.id)}
                        style={{ width: "15%" }}
                      />
                      <p style={{ fontSize: '10px', color: "#111" }}>{item.name} <br /> ({item.size})</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {viewMode === 'table' && showTable && !showUploader && (
            <div style={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th className='ps-4'>✔</th>
                    <th>Name</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Size</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item) => (
                    <tr
                      key={item.id}
                      onClick={() => toggleSelectMedia(item.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className='ps-4'>
                        <Form.Check
                          type="radio"
                          checked={selectedMedia.includes(item.id)}
                          onChange={() => toggleSelectMedia(item.id)}
                        />
                      </td>
                      <td className="d-flex align-items-center">
                        <img
                          src={item.thumbnail}
                          alt={item.name}
                          style={{ width: '40px', height: '40px', marginRight: '10px' }}
                        />
                        {item.name}
                      </td>
                      <td>{item.date}</td>
                      <td>{item.type}</td>
                      <td>{item.size}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {/* Pagination Controls */}
          {totalRecords > 0 && !showUploader && (
            <div className="d-flex justify-content-between align-items-center mt-3 px-2">
              <div>
                <small className="text-muted">
                  Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, totalRecords)} of {totalRecords} {filter !== 'All' ? filter.toLowerCase() : 'items'}
                </small>
              </div>
              {totalPages > 1 && (
                <Pagination size="sm" className="mb-0">
                  <Pagination.First
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  />
                  <Pagination.Prev
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  />

                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    // Show first page, last page, current page, and pages around current
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <Pagination.Item
                          key={pageNumber}
                          active={pageNumber === currentPage}
                          onClick={() => setCurrentPage(pageNumber)}
                        >
                          {pageNumber}
                        </Pagination.Item>
                      );
                    } else if (
                      pageNumber === currentPage - 2 ||
                      pageNumber === currentPage + 2
                    ) {
                      return <Pagination.Ellipsis key={pageNumber} disabled />;
                    }
                    return null;
                  })}

                  <Pagination.Next
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  />
                  <Pagination.Last
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="w-100 d-flex flex-column">
          {selectedMedia.length > 0 && !showUploader && (
            <div className="d-flex align-items-center mb-2">
              {mediaItems
                .filter((item) => selectedMedia.includes(item.id))
                .map((item) => (
                  <img
                    key={item.id}
                    src={item.thumbnail}
                    alt={item.name}
                    style={{
                      width: '50px',
                      height: '50px',
                      objectFit: 'cover',
                      marginRight: '10px',
                      borderRadius: '4px',
                    }}
                  />
                ))}
            </div>
          )}

          <div className="d-flex justify-content-between w-100">
            <Button variant="secondary" size="sm" onClick={resetSelection}>
              Reset
            </Button>
            <div>
              <Button variant="secondary" size="sm" onClick={() => {
                setLoading(false)
                onclose() // <-- FIXED
              }}>
                Close
              </Button>
              {uploadedFile && (
                <Button
                  variant="btn btn-outline-success"
                  size="sm"
                  className="ms-2"
                  disabled={uploading}
                  onClick={async () => {
                    if (!uploadedFile) return;
                    setUploading(true);
                    const file = uploadedFile.file;
                    const allowedExtensions = ['png', 'jpeg', 'jpg', 'gif'];

                    // Get max size based on file kind
                    const maxSize = getMaxFileSize(uploadedFile.kind);
                    const maxSizeInMB = getFileSizeInMB(uploadedFile.kind);
                    const fileExtension = file.name.split('.').pop().toLowerCase();

                    // Validate file size
                    if (file.size > maxSize) {
                      Swal.fire({
                        title: 'File Size Exceeded',
                        text: `${uploadedFile.kind.charAt(0).toUpperCase() + uploadedFile.kind.slice(1)} file size must be less than ${maxSizeInMB} MB.`,
                        icon: 'error',
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#3085d6',
                      });
                      setUploading(false);
                      return;
                    }
                    setLoading(true);
                    try {
                      const validLeadId = Number(leadId) > 0;
                      const directory = validLeadId ? `${userId}/Restricted/Lead/${leadId}/Documents/` : `${userId}/Restricted/Lead/`;
                      // const directory = `335/Restricted/kyc/`;
                      const token = gettoken; // Use the state variable

                      const result = await uploadbulkFile(file, directory, token);

                      const successMsg = typeof result === 'string' ? result : (result.Message || "Upload Successful");
                      setSuccess(successMsg);
                      setUploadedFile(null);

                      const fileName = file.name;
                      let updatedItems = [];
                      let found = false;

                      // Polling mechanism: try up to 3 times with 2s delay
                      for (let attempt = 1; attempt <= 3; attempt++) {
                        await new Promise(r => setTimeout(r, 2000));
                        updatedItems = await fetchMedia();

                        if (updatedItems && updatedItems.length > 0) {
                          const newItem = updatedItems.find(item => item.name.toLowerCase() === fileName.toLowerCase());
                          if (newItem) {
                            found = true;

                            // Manually ensure it's at the top (index 0) even if sorting is delayed
                            const otherItems = updatedItems.filter(item => item.id !== newItem.id);
                            setMediaItems([newItem, ...otherItems]);

                            setCurrentPage(1);
                            setSearchTerm('');
                            setFilter('All');
                            setSelectedMedia([newItem.id]);
                            break;
                          }
                        }
                      }

                      if (!found && updatedItems && updatedItems.length > 0) {
                        setCurrentPage(1);
                        setSearchTerm('');
                        setFilter('All');
                        setSelectedMedia([updatedItems[0].id]);
                      }

                    } catch (error) {
                      console.error('Error uploading file:', error);
                      setError("Upload failed. Please try again.")
                    } finally {
                      setLoading(false);
                      // Return to library panel
                      setShowUploader(false);
                      setUploading(false);
                    }
                  }}
                >
                  {uploading ? "Saving..." : "Save"}
                </Button>
              )}

              {selectedMedia.length > 0 && !showUploader && !uploadedFile && (
                <Button
                  variant="info"
                  size="sm"
                  className="ms-2"
                  onClick={() => {
                    const selectedItem = mediaItems.find(item => item.id === selectedMedia[0]);
                    if (!selectedItem) return;

                    // If channel is not RCS, skip image/video resolution validation
                    if (channel !== 'rcs') {
                      onSelectMedia({
                        ...selectedItem,
                        url: selectedItem.thumbnail,
                      });
                      setLoading(false);
                      onclose();
                      return;
                    }

                    // Only validate for image/video
                    const ext = selectedItem.name.split('.').pop().toLowerCase();
                    const isImage = ["jpg", "jpeg", "png", "gif"].includes(ext);
                    const isVideo = ["mp4"].includes(ext);

                    // --- Validation rules ---
                    // Defaults for rich card
                    // let requiredWidth = 1440, requiredHeight = 480, maxSize = 1 * 1024 * 1024;
                    let requiredWidth = "", requiredHeight = "", maxSize = "";
                    if (cardType === 'rich_card') {
                      if (cardOrientation === 'vertical' && cardHeight === 'short') {
                        requiredWidth = 1440; requiredHeight = 480;
                      } else if (cardOrientation === 'vertical' && cardHeight === 'medium') {
                        requiredWidth = 1440; requiredHeight = 720;
                      } else if (cardOrientation === 'horizontal' && mediaAlignment === 'left') {
                        requiredWidth = 768; requiredHeight = 1024;
                      } else if (cardOrientation === 'horizontal' && mediaAlignment === 'right') {
                        requiredWidth = 768; requiredHeight = 1024;
                      }
                      maxSize = isVideo ? 5 * 1024 * 1024 : 1 * 1024 * 1024;
                    }
                    else if (cardType === 'carousel' || cardType === 'carouselCard') {
                      maxSize = isVideo ? 5 * 1024 * 1024 : 1 * 1024 * 1024;
                      if (cardWidth === 'SMALL' && cardHeight === 'SHORT') {
                        requiredWidth = 960; requiredHeight = 768;
                      } else if (cardWidth === 'SMALL' && cardHeight === 'MEDIUM') {
                        requiredWidth = 576; requiredHeight = 720;
                      }
                      else if (cardWidth === 'MEDIUM' && cardHeight === 'SHORT') {
                        requiredWidth = 1440; requiredHeight = 720;
                      } else if (cardWidth === 'MEDIUM' && cardHeight === 'MEDIUM') {
                        requiredWidth = 1440; requiredHeight = 1080;
                      }
                    }

                    // Validate image
                    if (isImage) {
                      // Check size if available
                      if (selectedItem.size && Number(selectedItem.size) > maxSize) {
                        Swal.fire({
                          title: 'Image Size Exceeded',
                          text: `Image file size must be less than ${maxSize / 1024 / 1024} MB.`,
                          icon: 'error',
                          confirmButtonText: 'OK',
                          confirmButtonColor: '#3085d6',
                        });
                        return;
                      }
                      const img = new window.Image();
                      img.crossOrigin = 'Anonymous';
                      img.onload = function () {
                        if (!(img.width === requiredWidth && img.height === requiredHeight)) {
                          Swal.fire({
                            title: 'Invalid Image Resolution',
                            text: `The image you specify must have a resolution of ${requiredWidth} x ${requiredHeight} pixels.`,
                            icon: 'error',
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#3085d6',
                          });
                          return;
                        }
                        onSelectMedia({
                          ...selectedItem,
                          url: selectedItem.thumbnail,
                        });
                        setLoading(false);
                        onclose();
                      };
                      img.onerror = function () {
                        Swal.fire({
                          title: 'Image Load Error',
                          text: 'Could not load image for validation.',
                          icon: 'error',
                          confirmButtonText: 'OK',
                          confirmButtonColor: '#3085d6',
                        });
                      };
                      img.src = selectedItem.thumbnail;
                      return;
                    } else if (isVideo) {
                      if (selectedItem.size && Number(selectedItem.size) > maxSize) {
                        Swal.fire({
                          title: 'Video Size Exceeded',
                          text: `Video file size must be less than ${maxSize / 1024 / 1024} MB.`,
                          icon: 'error',
                          confirmButtonText: 'OK',
                          confirmButtonColor: '#3085d6',
                        });
                        return;
                      }
                      const video = document.createElement('video');
                      video.preload = 'metadata';
                      video.onloadedmetadata = function () {
                        if (!(video.videoWidth === requiredWidth && video.videoHeight === requiredHeight)) {
                          Swal.fire({
                            title: 'Invalid Video Resolution',
                            text: `The video you specify must have a resolution of ${requiredWidth} x ${requiredHeight} pixels.`,
                            icon: 'error',
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#3085d6',
                          });
                          return;
                        }
                        onSelectMedia({
                          ...selectedItem,
                          url: selectedItem.thumbnail,
                        });
                        setLoading(false);
                        onclose();
                      };
                      video.onerror = function () {
                        Swal.fire({
                          title: 'Video Load Error',
                          text: 'Could not load video for validation.',
                          icon: 'error',
                          confirmButtonText: 'OK',
                          confirmButtonColor: '#3085d6',
                        });
                      };
                      video.src = selectedItem.thumbnail;
                      return;
                    }
                    // For other types, just select
                    onSelectMedia({
                      ...selectedItem,
                      url: selectedItem.thumbnail,
                    });
                    setLoading(false);
                    onclose();
                  }}
                >
                  Select
                </Button>
              )}


            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MediaLibrary;